import type { HelpTopic } from '../core/index.js'

/** The messages module's help topics (docs/plans/in-app-help-plan.md). */
export const MESSAGES_HELP_TOPICS: readonly HelpTopic[] = [
  {
    id: 'messages.worker-barriers',
    title: 'What’s getting in the way?',
    seats: ['worker'],
    summary: 'A note to your facility, in your own words, about what makes a shift or a behavior hard for you.',
    sections: [
      {
        heading: 'What you can do here',
        points: [
          'Write a note in general, or tie it to one of the bars that is short of full marks.',
          'Your facility reads it beside your score and can reply. A reply comes to your Messages.',
          'You can have one unread note per bar, and one general note, at a time.',
        ],
      },
      {
        heading: 'Good to know',
        points: ['Nobody else is named or compared. The note is about you and what would help.', 'Writing a note never changes your score.'],
      },
    ],
    related: ['scoring.worker-home', 'messages.worker-messages'],
  },
  {
    id: 'messages.worker-contacts',
    title: 'Notifications',
    seats: ['worker'],
    summary: 'Choose how autonoME reaches you when something needs your answer while you are not in the app.',
    sections: [
      {
        heading: 'What you can do here',
        points: [
          'Turn on alerts on this device for alerts on your phone or computer.',
          'Add an email address. We send a six-digit code to check it is yours.',
          'Switch any of them off, or forget them, at any time.',
          'Where texts are offered, add your mobile number the same way. Replying STOP to any text switches texts off here; replying START, or the switch on this card, turns them back on.',
          'Your facility may enter your mobile number for you. It is texted one invitation and nothing else until you agree: reply YES to the text, or tap Agree and verify here (a code proves the number). Not me forgets it.',
          'Once your number is verified, four switches say what reaches you by text: open shifts and swaps, your schedule and bidding, your score and coaching, replies from your facility. Everything is still in the app. Your facility chooses which kinds of message it texts at all, and these are yours within that.',
          'Open shifts and swaps stay on by text while you are enrolled for a Clinical Bridge day still to come: the enrollment is a promise to answer.',
          'Your facility may keep quiet hours. A text that falls in them is held to the end, and an open shift or a swap offer comes through anyway, since it cannot wait for morning.',
        ],
      },
      {
        heading: 'What reaches you',
        points: [
          'An alert comes at once when a Clinical Bridge shift opens, when an open shift or a swap is offered to you, when your month’s schedule is released or changes, when your facility reminds you to finish your month, and when your facility replies to you.',
          'Everything else waits in your Messages.',
          'An alert says only that something is waiting and opens the app. Your score, your tier and your pay never leave autonoME.',
        ],
      },
      {
        heading: 'Good to know',
        points: ['On an iPhone, add autonoME to your home screen first: tap Share, then Add to Home Screen, and open it from there.', 'Text messages are coming later.'],
      },
    ],
    related: ['messages.worker-messages'],
  },
  {
    id: 'messages.worker-messages',
    title: 'Messages',
    seats: ['worker'],
    summary: 'Everything autonoME and your facility have told you, newest first.',
    sections: [
      {
        heading: 'What you see',
        points: [
          'Coaching about your score: a streak, the next tier within reach, or the one thing most worth working on. At most one a day.',
          'Your schedule: the released month, swaps and open shifts offered to you, and changes to your shifts.',
          'Clinical Bridge shifts, and your facility’s replies to your notes.',
        ],
      },
      {
        heading: 'Good to know',
        points: ['A message that is no longer true, like a shift someone else took, is withdrawn.', 'Messages come from your own shifts and nothing else. Nobody is compared with anyone.'],
      },
    ],
    related: ['messages.worker-contacts'],
  },
  {
    id: 'messages.facility-delivery',
    title: 'Notifications',
    seats: ['operator', 'facility_administrator'],
    summary: 'Which kinds of message this facility texts to workers, and when. Every message is in the worker\'s app first; a text is a nudge towards it.',
    sections: [
      {
        heading: 'What you can set here',
        points: [
          'Text workers from this facility: the switch. Off, nothing is texted from this facility, whatever a worker chose.',
          'Quiet hours, in the facility\'s time zone. A text that falls in them is held and sent at the end. They may cross midnight.',
          'One switch per kind of message, laid out by the four groups a worker chooses by. "Default" marks the deployment\'s own policy for that kind; switch a kind on to text it beyond the policy, or off to keep it in the app here.',
          'Save with the rest of the configuration. Every change is in the configuration history.',
        ],
      },
      {
        heading: 'What goes through quiet hours',
        points: [
          'A Clinical Bridge callout and its end, a swap offer and an open shift run on a clock and are lost if they wait for morning, so they are texted at once. They wear an Urgent mark in the list.',
          'A held text goes once quiet hours end if the message still stands and the worker\'s number is still on. One withdrawn or expired meanwhile is dropped, with a receipt saying so on the roster row\'s Messages tab.',
        ],
      },
      {
        heading: 'The worker\'s side',
        points: [
          'A worker chooses which of the four groups reach them by text on their own Notifications card. The facility says what may be texted; the worker says what they want within that. Neither widens the other.',
          'A worker who replies STOP is texted nothing more until they reply START or switch texts back on in the app.',
          'A facility administrator reads these settings; an operator changes them.',
        ],
      },
    ],
    related: ['messages.worker-contacts'],
  },
  {
    id: 'messages.message-workers',
    title: 'Message workers',
    seats: ['operator', 'facility_administrator'],
    summary: 'Your own words to one worker or the roster. Each gets it in their app, and by text where the facility and the worker allow.',
    sections: [
      {
        heading: 'What you can do here',
        points: [
          'Write up to 320 characters (two text segments) and send it to everyone the filter matches - a role, a tier, a unit, an employment type - or, from a roster row\'s Messages tab, to one worker.',
          'The message goes as written, with the facility\'s name before it and your name beside it in the app. Nothing is added: no score, no tier, no pay, nobody else\'s name. What it says is yours.',
          'Urgent sends through the facility\'s quiet hours and the worker\'s daily cap of 20 texts, like an open shift. Otherwise a text waits for morning.',
          'A message to the roster is confirmed with the count, and at most one goes to the roster an hour.',
        ],
      },
      {
        heading: 'Where it went',
        points: [
          'Every message you sent is listed below the box, newest first. Open one to see how it reached people: in the app, texted, held for quiet hours, not texted (the worker or the facility switched that kind off), or with no number.',
          'A worker is texted only where the facility texts messages from staff (the Notifications tab of Configuration) and the worker has that group on.',
          'The activity feed records that you wrote, and to how many, never the words.',
          'A worker who replies to any text with their own words is heard: the reply is listed under Replies by text on their roster row\'s Messages tab, and they are texted once an hour at most that the facility will see it.',
        ],
      },
    ],
    related: ['messages.facility-delivery'],
  },
]
